/**
 * What a researcher wrote about a subject.
 *
 * **The one panel showing something stored rather than derived.** Profiles, interests and
 * the timeline are queries over what sources said; a note is what a researcher thinks, and
 * nothing computes that.
 *
 * It is also the most candid personal data the system holds, which is worth saying on the
 * screen where somebody is about to type. A note about a named person is subject to the
 * same right of erasure as everything else here, and it goes with the subject when that
 * right is exercised.
 *
 * Withdrawing keeps the row so a mis-click is recoverable, and the list simply stops
 * showing it — the recoverability is for an operator with the database, not a third state
 * every reader has to reason about.
 */

import { useState, type ReactElement } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { request } from "@/lib/api";
import { Button, EmptyState, ErrorState, Loading } from "@/components/ui";
import { useCan } from "@/hooks/usePermissions";
import type { WriteNoteRequest } from '@/lib/schemas';

interface SubjectNote {
  id: string;
  subject_id: string;
  body: string;
  author_id: string;
  created_at: string;
  updated_at: string;
}

/**
 * The notes panel.
 *
 * @param props - The subject to read.
 * @returns The panel.
 */
export function SubjectNotesPanel({ subjectId }: { subjectId: string }): ReactElement {
  const canEdit = useCan("create_run_cases");
  const queryClient = useQueryClient();
  const [body, setBody] = useState("");

  const notes = useQuery({
    queryKey: ["subject-notes", subjectId],
    queryFn: () => request<SubjectNote[]>(`/research/subjects/${subjectId}/notes`),
  });

  const write = useMutation({
    mutationFn: () =>
      request<SubjectNote, WriteNoteRequest>(`/research/subjects/${subjectId}/notes`, {
        method: "POST",
        body: { body },
      }),
    onSuccess: () => {
      setBody("");
      void queryClient.invalidateQueries({ queryKey: ["subject-notes", subjectId] });
    },
  });

  const withdraw = useMutation({
    mutationFn: (noteId: string) =>
      request<void>(`/research/subjects/${subjectId}/notes/${noteId}`, { method: "DELETE" }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["subject-notes", subjectId] }),
  });

  if (notes.isLoading) return <Loading label="Loading notes" />;
  if (notes.error != null) return <ErrorState error={notes.error} />;

  const rows = notes.data ?? [];

  return (
    <section className="space-y-3" aria-label="Notes panel" data-testid="subject-panel-notes">
      <p className="max-w-2xl text-xs text-muted">
        Your working notes about this person. They are personal data about them like
        anything else here: erasing the subject erases these too, and they are readable by
        anyone in your tenant who can see the subject.
      </p>

      {canEdit && (
        <div className="space-y-2">
          <textarea
            rows={3}
            className="w-full rounded border border-line bg-surface px-2 py-1.5 text-sm"
            data-testid="note-body"
            placeholder="What did you conclude, and from what?"
            value={body}
            onChange={(event) => setBody(event.target.value)}
          />
          <Button
            tone="primary"
            data-testid="note-save"
            disabled={body.trim() === "" || write.isPending}
            onClick={() => write.mutate()}
          >
            {write.isPending ? "Saving…" : "Add note"}
          </Button>
          {write.error != null && <ErrorState error={write.error} />}
        </div>
      )}

      {rows.length === 0 ? (
        <EmptyState>No notes yet.</EmptyState>
      ) : (
        <ul className="space-y-2" data-testid="note-list">
          {rows.map((note) => (
            <li
              key={note.id}
              className="rounded border border-line p-3 text-sm"
              data-testid="note-row"
            >
              <p className="whitespace-pre-wrap">{note.body}</p>
              <div className="mt-2 flex items-center justify-between text-xs text-muted">
                <span>{note.created_at.slice(0, 19).replace("T", " ")}</span>
                {canEdit && (
                  <button
                    type="button"
                    className="hover:text-danger"
                    data-testid="note-withdraw"
                    onClick={() => withdraw.mutate(note.id)}
                  >
                    Withdraw
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
